import type {SuggestRequest, SuggestValue as SuggestValueType} from '@logistics-frontend/blocks'
import {applySpec, flow, get, identity, map} from '@logistics-frontend/utils'
import {toPoint} from '@yandex-taxi/react-integration-api'

import {type SuggestBody, type SuggestObject, type SuggestResponse, SuggestAction} from 'src/shared/types'

type SuggestValue = SuggestValueType<SuggestObject>
type SpecificProps = Pick<SuggestBody, 'position' | 'sticky' | 'state' | 'part' | 'prev_log'>

function uniqID(suggestObject: SuggestObject): string {
  return suggestObject.uri || `${suggestObject.pos}.${suggestObject.text}`
}

const toSuggestValue: (suggestObject: SuggestObject) => SuggestValue = applySpec<SuggestValue>({
  id: uniqID,
  title: get('title.text'),
  subtitle: get('subtitle.text'),
  distance: get('distance.text'),
  __origin: identity,
})

export const toSuggest: (response: SuggestResponse) => SuggestValue[] = flow(get('results'), map(toSuggestValue))

export function toSuggestBody(request: SuggestRequest<SuggestObject>): SuggestBody {
  const {type, __origin: geoObject, text, point, location, action} = request
  const defaultState = {
    accuracy: 0,
    location,
  }

  function createActionPayload(actionType: SuggestAction, specificProps: SpecificProps = {}) {
    return {
      type,
      action: actionType,
      position: specificProps.position || point || location,
      state: defaultState,
      ...specificProps,
    }
  }

  if (action === SuggestAction.PIN_DROP) {
    return createActionPayload(SuggestAction.PIN_DROP, {
      position: point,
      sticky: false,
      state: {
        fields: [],
      },
    })
  }

  if (geoObject) {
    if (geoObject.action === 'substitute') {
      return createActionPayload(SuggestAction.USER_SELECT, {
        part: geoObject.text,
        prev_log: geoObject.log,
      })
    }

    return createActionPayload(SuggestAction.FINALIZE, {
      position: toPoint(geoObject.pos),
      prev_log: geoObject.log,
      sticky: true,
    })
  }

  if (text) {
    return createActionPayload(SuggestAction.USER_INPUT, {
      part: text,
      position: point,
      sticky: false,
    })
  }

  return createActionPayload(SuggestAction.ZERO_SUGGEST, {
    sticky: false,
  })
}
